import { useEffect, useRef, useState } from "react";
import { getAdminVipPackages, mailVipPackages, unmailVipPackages, shipVipCard, getAdminDealStages, logAuditEvent } from "../../../api/api-hook";
import { getAdminSession } from "../../../utils/adminAuth";
import { HUBSPOT_IDS, hubspotRecordUrl } from "../../../utils/hubspotIds";

type VipPackage = {
  dealId: string;
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  ghinNumber: string;
  vipCardNumber: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  vipPackage: string;
  vipCardType: string;
  dealstage: string;
  amount: string;
  amountRaw: number;
  createDate: string;
  mailedDate: string;
  mailedAt: string;
  cardShipped: string;
  cardShippedDate: string;
};

type DealStage = { id: string; label: string };

const BRAND_GREEN   = "#023822";
const BRAND_GOLD    = "#C9A24D";

const formatDate = (iso: string) => {
  if (!iso) return "—";
  // HubSpot returns date properties as "YYYY-MM-DD"; epoch ms strings also supported
  const d = /^\d{10,}$/.test(iso) ? new Date(Number(iso)) : new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" });
};

const classify = (pkg: string) => {
  const p = (pkg || "").toLowerCase();
  if (p === "wv vip card" || p === "wv-vip-card") return "wv-vip";
  if (p.includes("gold"))   return "gold";
  if (p.includes("silver")) return "silver";
  if (p.includes("bronze")) return "bronze";
  return "vip";
};

const StageBadge = ({ dealstage, stages }: { dealstage: string; stages: DealStage[] }) => {
  const isRefunded = dealstage === HUBSPOT_IDS.refundedStage;
  const label = isRefunded
    ? "Refunded"
    : stages.find((s) => s.id === dealstage)?.label || dealstage || "—";
  const bg    = isRefunded ? "rgba(220,53,69,0.1)"  : "rgba(25,135,84,0.1)";
  const color = isRefunded ? "#b02a37"              : "#146c43";
  return (
    <span style={{
      display: "inline-block",
      padding: "0.2rem 0.6rem",
      borderRadius: 20,
      fontSize: "0.72rem",
      fontWeight: 600,
      backgroundColor: bg,
      color,
      whiteSpace: "nowrap",
      letterSpacing: "0.02em",
    }}>
      {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// PackageTable
// ---------------------------------------------------------------------------
interface PackageTableProps {
  title: string;
  rows: VipPackage[];
  stages: DealStage[];
  selected: Set<string>;
  onToggleOne: (id: string) => void;
  onToggleAll: (ids: string[], selectAll: boolean) => void;
  onExportAndMail: (tableRows: VipPackage[]) => void;
  onUnpublish: (tableRows: VipPackage[]) => void;
  mailing: boolean;
  unpublishing: boolean;
  showMailed: boolean;
  onShipCard: (row: VipPackage, date: string) => Promise<void>;
  shipping: boolean;
  onBulkShip: (rows: VipPackage[], date: string) => Promise<void>;
  bulkShipping: boolean;
}

const thStyle: React.CSSProperties = {
  padding: "0.7rem 0.75rem",
  fontWeight: 600,
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#6c757d",
  borderBottom: "2px solid #e9ecef",
  whiteSpace: "nowrap",
  background: "#f8f9fa",
};

const tdStyle: React.CSSProperties = {
  padding: "0.65rem 0.75rem",
  fontSize: "0.875rem",
  verticalAlign: "middle",
  borderBottom: "1px solid #f0f0f0",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const PackageTable = ({ title, rows, stages, selected, onToggleOne, onToggleAll, onExportAndMail, onUnpublish, mailing, unpublishing, showMailed, onShipCard, shipping, onBulkShip, bulkShipping }: PackageTableProps) => {
  const ids      = rows.map((r) => r.dealId);
  const allSel   = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSel  = ids.some((id) => selected.has(id));
  const selCount = ids.filter((id) => selected.has(id)).length;
  const cbRef    = useRef<HTMLInputElement>(null);
  const [openPickerFor, setOpenPickerFor] = useState<string | null>(null);
  const [pickDate, setPickDate] = useState("");
  const [bulkShipDate, setBulkShipDate] = useState(new Date().toISOString().split("T")[0]);

  useEffect(() => {
    if (cbRef.current) cbRef.current.indeterminate = someSel && !allSel;
  }, [someSel, allSel]);

  if (rows.length === 0) return null;

  return (
    <div style={{ marginBottom: "2rem" }}>
      <div className="d-flex align-items-center mb-2" style={{ gap: "0.6rem" }}>
        <h6 style={{ margin: 0, fontWeight: 700, color: BRAND_GREEN, fontSize: "0.9rem", letterSpacing: "-0.2px" }}>
          {title}
          <span style={{ marginLeft: "0.4rem", fontWeight: 400, color: "#8a9199", fontSize: "0.82rem" }}>
            ({rows.length})
          </span>
        </h6>
        {!showMailed && (
          <button
            onClick={() => onExportAndMail(rows)}
            disabled={selCount === 0 || mailing}
            style={{
              backgroundColor: selCount > 0 ? BRAND_GOLD : "#e9ecef",
              color: selCount > 0 ? "#fff" : "#adb5bd",
              border: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 6,
              padding: "0.25rem 0.75rem",
              cursor: selCount > 0 ? "pointer" : "default",
              whiteSpace: "nowrap",
            }}
          >
            {mailing ? "Processing…" : "Export & Mail"}
          </button>
        )}
        {showMailed && (
          <button
            onClick={() => onUnpublish(rows)}
            disabled={selCount === 0 || unpublishing}
            style={{
              backgroundColor: selCount > 0 ? "#dc3545" : "#e9ecef",
              color: selCount > 0 ? "#fff" : "#adb5bd",
              border: "none",
              fontWeight: 600,
              fontSize: "0.75rem",
              borderRadius: 6,
              padding: "0.25rem 0.75rem",
              cursor: selCount > 0 ? "pointer" : "default",
              whiteSpace: "nowrap",
            }}
          >
            {unpublishing ? "Processing…" : "Unpublish"}
          </button>
        )}
        {showMailed && (
          <>
            <button
              onClick={() => onToggleAll(ids, !allSel)}
              style={{
                background: "none",
                border: "1px solid #ced4da",
                borderRadius: 5,
                color: "#495057",
                cursor: "pointer",
                padding: "0.2rem 0.6rem",
                fontSize: "0.75rem",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {allSel ? "Deselect All" : "Select All"}
            </button>
            <input
              type="date"
              value={bulkShipDate}
              onChange={(e) => setBulkShipDate(e.target.value)}
              style={{ fontSize: "0.78rem", padding: "0.2rem 0.4rem", borderRadius: 4, border: "1px solid #ced4da", outline: "none" }}
            />
            <button
              onClick={async () => {
                if (!bulkShipDate) return;
                const qualifying = rows.filter((r) => selected.has(r.dealId) && r.cardShipped !== "Yes");
                if (qualifying.length === 0) return;
                await onBulkShip(qualifying, bulkShipDate);
              }}
              disabled={!bulkShipDate || selCount === 0 || bulkShipping}
              style={{
                backgroundColor: (bulkShipDate && selCount > 0) ? BRAND_GREEN : "#e9ecef",
                color: (bulkShipDate && selCount > 0) ? "#fff" : "#adb5bd",
                border: "none",
                fontWeight: 600,
                fontSize: "0.75rem",
                borderRadius: 6,
                padding: "0.25rem 0.75rem",
                cursor: (bulkShipDate && selCount > 0) ? "pointer" : "default",
                whiteSpace: "nowrap",
              }}
            >
              {bulkShipping ? "Applying…" : "Apply Ship Date"}
            </button>
          </>
        )}
        {selCount > 0 && (
          <span style={{ fontSize: "0.78rem", color: "#6c757d" }}>{selCount} selected</span>
        )}
      </div>
      <div style={{ borderRadius: 12, border: "1px solid #e9ecef", overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700, tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 40 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: "16%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 100 }} />
            {showMailed && <col style={{ width: 160 }} />}
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: "center" }}>
                <input
                  type="checkbox"
                  ref={cbRef}
                  checked={allSel}
                  onChange={() => onToggleAll(ids, !allSel)}
                  title="Select all"
                />
              </th>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Name</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>GHIN</th>
              <th style={thStyle}>VIP Card #</th>
              <th style={thStyle}>Package</th>
              <th style={thStyle}>Stage</th>
              <th style={thStyle}>Amount</th>
              <th style={thStyle}>Date</th>
              {showMailed && <th style={thStyle}>Card Shipped</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((t, i) => {
              const isSelected = selected.has(t.dealId);
              return (
                <tr
                  key={t.dealId}
                  style={{
                    backgroundColor: isSelected ? "rgba(2,56,34,0.06)" : i % 2 === 0 ? "#fff" : "#fafafa",
                    cursor: "pointer",
                  }}
                  onClick={() => onToggleOne(t.dealId)}
                >
                  <td style={{ ...tdStyle, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleOne(t.dealId)}
                    />
                  </td>
                  <td style={{ ...tdStyle }} onClick={(e) => e.stopPropagation()}>
                    {t.contactId ? (
                      <a
                        href={hubspotRecordUrl("contact", t.contactId)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none"
                        style={{ color: BRAND_GREEN, fontWeight: 600, fontSize: "0.875rem" }}
                      >
                        {i + 1}
                      </a>
                    ) : <span style={{ color: "#adb5bd" }}>{i + 1}</span>}
                  </td>
                  <td style={{ ...tdStyle, fontWeight: 500 }} onClick={(e) => e.stopPropagation()}>
                    {t.contactId ? (
                      <a
                        href={hubspotRecordUrl("contact", t.contactId)}
                        target="_blank"
                        rel="noreferrer"
                        className="text-decoration-none"
                        style={{ color: BRAND_GREEN, fontWeight: 500 }}
                      >
                        {t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"}
                      </a>
                    ) : (
                      t.firstName || t.lastName ? `${t.firstName} ${t.lastName}`.trim() : "—"
                    )}
                    {t.mailedDate && (
                      <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: "#198754", fontWeight: 400 }}>
                        ✓ Mailed
                      </span>
                    )}
                  </td>
                  <td style={{ ...tdStyle, color: "#495057" }}>{t.email || "—"}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.82rem" }}>{t.ghinNumber || "—"}</td>
                  <td style={{ ...tdStyle, fontFamily: "monospace", fontSize: "0.82rem" }}>{t.vipCardNumber || "—"}</td>
                  <td style={{ ...tdStyle, color: "#495057" }}>{t.vipPackage || "—"}</td>
                  <td style={tdStyle}>
                    <StageBadge dealstage={t.dealstage} stages={stages} />
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    {t.amountRaw > 0 ? t.amount : "$0.00"}
                  </td>
                  <td style={{ ...tdStyle, whiteSpace: "nowrap", color: "#6c757d", fontSize: "0.8rem" }}>
                    {formatDate(t.createDate)}
                  </td>
                  {showMailed && (
                    <td style={{ ...tdStyle }} onClick={(e) => e.stopPropagation()}>
                      {openPickerFor === t.dealId ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem" }}>
                          <input
                            type="date"
                            value={pickDate}
                            onChange={(e) => setPickDate(e.target.value)}
                            style={{ fontSize: "0.78rem", padding: "0.2rem 0.4rem", borderRadius: 4, border: "1px solid #ced4da", outline: "none", width: "100%" }}
                            autoFocus
                          />
                          <div style={{ display: "flex", gap: "0.3rem" }}>
                            <button
                              onClick={async () => {
                                if (!pickDate) return;
                                await onShipCard(t, pickDate);
                                setOpenPickerFor(null);
                              }}
                              disabled={!pickDate || shipping}
                              style={{
                                background: BRAND_GREEN, color: "#fff", border: "none", borderRadius: 4,
                                padding: "0.2rem 0.6rem", fontSize: "0.75rem", fontWeight: 600, cursor: pickDate ? "pointer" : "default",
                              }}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setOpenPickerFor(null)}
                              style={{ background: "none", border: "none", color: "#6c757d", cursor: "pointer", fontSize: "0.8rem", padding: "0.1rem 0.25rem" }}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ) : t.cardShipped === "Yes" ? (
                        <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                          <button
                            onClick={() => {
                              const existing = t.cardShippedDate
                                ? (() => { const d = new Date(Number(t.cardShippedDate)); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; })()
                                : "";
                              setOpenPickerFor(t.dealId);
                              setPickDate(existing);
                            }}
                            title="Edit ship date"
                            style={{ background: "none", border: "none", color: "#198754", fontSize: "0.8rem", cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: "0.3rem" }}
                          >
                            ✓ {t.cardShippedDate ? formatDate(t.cardShippedDate) : "Shipped"}
                            <i className="bi bi-pencil-fill" style={{ fontSize: "0.7rem", color: "#6c757d" }} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => {
                            const today = new Date().toISOString().split("T")[0];
                            setOpenPickerFor(t.dealId);
                            setPickDate(today);
                          }}
                          title="Set ship date"
                          style={{
                            background: "none", border: "1px dashed #ced4da", borderRadius: 5,
                            color: "#6c757d", cursor: "pointer", padding: "0.2rem 0.5rem",
                            fontSize: "0.78rem", display: "flex", alignItems: "center", gap: "0.3rem",
                          }}
                        >
                          <i className="bi bi-box-seam" /> Set Ship Date
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// BulkVipPackages
// ---------------------------------------------------------------------------
type TabId = "digital" | "paper";

const BulkVipPackages = () => {
  const [packages, setPackages]     = useState<VipPackage[]>([]);
  const [stages, setStages]         = useState<DealStage[]>([]);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [search, setSearch]         = useState("");
  const [showMailed, setShowMailed] = useState(false);
  const [dateFrom, setDateFrom]     = useState("");
  const [dateTo, setDateTo]         = useState("");
  const [activeTab, setActiveTab]   = useState<TabId>("digital");
  const [selected, setSelected]     = useState<Set<string>>(new Set());
  const [mailing, setMailing]           = useState(false);
  const [mailError, setMailError]       = useState<string | null>(null);
  const [unpublishing, setUnpublishing] = useState(false);
  const [unpubError, setUnpubError]     = useState<string | null>(null);
  const [shipping, setShipping]         = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    setError(null);
    try {
      const [pkgResp, stageResp] = await Promise.all([getAdminVipPackages(), getAdminDealStages()]);
      if (pkgResp.success) {
        setPackages(pkgResp.packages);
        setSelected(new Set());
      } else {
        setError(pkgResp.message || "Failed to load VIP packages.");
        setPackages([]);
      }
      if (stageResp.success) setStages(stageResp.stages);
    } catch {
      setError("Network error. Please try again.");
      setPackages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPackages(); }, []);
  useEffect(() => { setSelected(new Set()); }, [activeTab, showMailed]);

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = (ids: string[], selectAll: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (selectAll) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const handleExportAndMail = async (tableRows: VipPackage[]) => {
    const toMail = tableRows.filter((t) => selected.has(t.dealId) && classify(t.vipPackage) !== "wv-vip");
    if (toMail.length === 0) return;

    setMailing(true);
    setMailError(null);
    try {
      const dealIds = toMail.map((t) => t.dealId);

      const headers = ["Name", "Email", "GHIN", "VIP Card #", "Package", "Type", "Street Address", "Address 2", "City", "State", "Zip", "Amount"];
      const rows    = toMail.map((t) => [
        `${t.firstName} ${t.lastName}`.trim(),
        t.email, t.ghinNumber, t.vipCardNumber, t.vipPackage, t.vipCardType,
        t.address, t.address2, t.city, t.state, t.zip, t.amount,
      ]);
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = `vip-packages-${activeTab}-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      const resp = await mailVipPackages(dealIds);
      if (!resp.success) throw new Error(resp.message || "Failed to mark as mailed.");

      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "VIP Packages Mailed",
        details: `${dealIds.length} ${activeTab} package${dealIds.length !== 1 ? "s" : ""} marked as mailed`,
      });
      setPackages((prev) =>
        prev.map((t) => dealIds.includes(t.dealId) ? { ...t, mailedDate: new Date().toISOString() } : t)
      );
      setSelected((prev) => {
        const next = new Set(prev);
        dealIds.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err: any) {
      setMailError(err?.response?.data?.message || err?.message || "Failed to export and mail.");
    } finally {
      setMailing(false);
    }
  };

  const handleUnpublish = async (tableRows: VipPackage[]) => {
    const toUnpublish = tableRows.filter((t) => selected.has(t.dealId));
    if (toUnpublish.length === 0) return;
    setUnpublishing(true);
    setUnpubError(null);
    try {
      const dealIds = toUnpublish.map((t) => t.dealId);
      const resp = await unmailVipPackages(dealIds);
      if (!resp.success) throw new Error(resp.message || "Failed to unpublish.");
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "VIP Packages Unmarked",
        details: `${dealIds.length} package${dealIds.length !== 1 ? "s" : ""} removed from mailed`,
      });
      setPackages((prev) => prev.filter((t) => !dealIds.includes(t.dealId)));
      setSelected((prev) => {
        const next = new Set(prev);
        dealIds.forEach((id) => next.delete(id));
        return next;
      });
    } catch (err: any) {
      setUnpubError(err?.response?.data?.message || err?.message || "Failed to unpublish.");
    } finally {
      setUnpublishing(false);
    }
  };

  const visible = packages.filter((t) => {
    if (!showMailed && t.mailedDate) return false;
    if (showMailed && !t.mailedDate) return false;
    if (dateFrom && t.createDate) {
      if (t.createDate.slice(0, 10) < dateFrom) return false;
    }
    if (dateTo && t.createDate) {
      if (t.createDate.slice(0, 10) > dateTo) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (t.firstName  || "").toLowerCase().includes(q) ||
      (t.lastName   || "").toLowerCase().includes(q) ||
      (t.email      || "").toLowerCase().includes(q) ||
      (t.ghinNumber || "").toLowerCase().includes(q) ||
      (t.vipCardNumber || "").toLowerCase().includes(q) ||
      (t.vipPackage    || "").toLowerCase().includes(q)
    );
  });

  const digital = visible.filter((t) => (t.vipCardType || "").toLowerCase() === "digital");
  const paper   = visible.filter((t) => (t.vipCardType || "").toLowerCase() === "paper");

  const digitalSilver  = digital.filter((t) => classify(t.vipPackage) === "silver");
  const digitalGold    = digital.filter((t) => classify(t.vipPackage) === "gold");

  const paperVipBronze = paper.filter((t) => ["vip", "bronze"].includes(classify(t.vipPackage)));
  const paperSilver    = paper.filter((t) => classify(t.vipPackage) === "silver");
  const paperGold      = paper.filter((t) => classify(t.vipPackage) === "gold");
  const paperWvVip     = paper.filter((t) => classify(t.vipPackage) === "wv-vip");
  const replacement    = visible.filter((t) => (t.vipCardType || "").toLowerCase() === "replacement");

  const tabCount = (tab: TabId) =>
    tab === "digital"
      ? digitalSilver.length + digitalGold.length
      : paperVipBronze.length + paperSilver.length + paperGold.length + paperWvVip.length + replacement.length;

  const emptyMessage = showMailed
    ? "All caught up — no mailed packages here."
    : "Nothing to mail — all packages have been exported.";

  const handleShipCard = async (row: VipPackage, date: string) => {
    setShipping(true);
    try {
      // Combine the picked date with the current wall-clock time in the user's local timezone
      const [y, m, d] = date.split("-").map(Number);
      const now = new Date();
      const localEpoch = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()).getTime();
      const resp = await shipVipCard(row.dealId, localEpoch);
      if (!resp.success) throw new Error(resp.message || "Failed to update.");
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "VIP Card Shipped",
        details: `Card marked as shipped for ${row.firstName} ${row.lastName} (${row.email}) on ${date}`,
      });
      setPackages((prev) =>
        prev.map((p) =>
          p.dealId === row.dealId
            ? { ...p, cardShipped: "Yes", cardShippedDate: String(localEpoch) }
            : p
        )
      );
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || "Failed to update card shipped.");
    } finally {
      setShipping(false);
    }
  };

  const handleBulkShipCard = async (rows: VipPackage[], date: string) => {
    setShipping(true);
    try {
      const [y, m, d] = date.split("-").map(Number);
      const now = new Date();
      const localEpoch = new Date(y, m - 1, d, now.getHours(), now.getMinutes(), now.getSeconds(), now.getMilliseconds()).getTime();
      await Promise.all(rows.map((row) => shipVipCard(row.dealId, localEpoch)));
      logAuditEvent({
        actor:   getAdminSession()?.displayName || "Admin",
        action:  "bulk_action",
        label:   "VIP Cards Ship Date Applied (Bulk)",
        details: `Ship date ${date} applied to ${rows.length} card${rows.length !== 1 ? "s" : ""}`,
      });
      const updatedIds = new Set(rows.map((r) => r.dealId));
      setPackages((prev) =>
        prev.map((p) =>
          updatedIds.has(p.dealId) ? { ...p, cardShipped: "Yes", cardShippedDate: String(localEpoch) } : p
        )
      );
    } catch (err: any) {
      alert(err?.response?.data?.message || err?.message || "Failed to apply ship dates.");
    } finally {
      setShipping(false);
    }
  };

  const sharedTableProps = {
    stages,
    selected,
    onToggleOne: toggleOne,
    onToggleAll: toggleAll,
    onExportAndMail: handleExportAndMail,
    onUnpublish: handleUnpublish,
    mailing,
    unpublishing,
    showMailed,
    onShipCard: handleShipCard,
    shipping,
    onBulkShip: handleBulkShipCard,
    bulkShipping: shipping,
  };

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
        <div>
          <h4 className="mb-1 fw-bold" style={{ color: BRAND_GREEN, letterSpacing: "-0.3px" }}>VIP Packages</h4>
          <p className="mb-0" style={{ fontSize: "0.82rem", color: "#8a9199" }}>
            <span style={{ fontWeight: 600, color: "#495057" }}>
            {packages.filter((t) => {
              if (t.mailedDate) return false;
              const type = (t.vipCardType || "").toLowerCase();
              const cat  = classify(t.vipPackage);
              if (type === "digital")     return cat === "silver" || cat === "gold";
              if (type === "paper")       return true;
              return false;
            }).length}
          </span> pending mail
          </p>
        </div>
      </div>

      {error      && <div className="alert alert-danger small">{error}</div>}
      {mailError  && <div className="alert alert-danger small">{mailError}</div>}
      {unpubError && <div className="alert alert-danger small">{unpubError}</div>}

      <div style={{ background: "#f8f9fa", borderRadius: 12, padding: "0.85rem 1rem", marginBottom: "1.25rem", border: "1px solid #e9ecef" }}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search name, email, GHIN, card number…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ maxWidth: 300, borderRadius: 8 }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <span style={{ fontSize: "0.78rem", color: "#6c757d", whiteSpace: "nowrap" }}>From</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{ maxWidth: 148, borderRadius: 8 }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.35rem" }}>
            <span style={{ fontSize: "0.78rem", color: "#6c757d", whiteSpace: "nowrap" }}>To</span>
            <input
              type="date"
              className="form-control form-control-sm"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{ maxWidth: 148, borderRadius: 8 }}
            />
          </div>
          {(dateFrom || dateTo) && (
            <button
              onClick={() => { setDateFrom(""); setDateTo(""); }}
              style={{ background: "none", border: "none", color: "#6c757d", fontSize: "0.78rem", cursor: "pointer", padding: "0.1rem 0.25rem" }}
              title="Clear date filter"
            >
              ✕ Clear
            </button>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: "0.82rem", color: "#6c757d", cursor: "pointer", userSelect: "none" }}>
            <input
              type="checkbox"
              checked={showMailed}
              onChange={(e) => setShowMailed(e.target.checked)}
            />
            Show mailed
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "2px solid #e9ecef", marginBottom: "1.5rem" }}>
        {(["digital", "paper"] as TabId[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              background: "none",
              border: "none",
              borderBottom: activeTab === tab ? `2px solid ${BRAND_GREEN}` : "2px solid transparent",
              marginBottom: -2,
              padding: "0.55rem 1.25rem",
              fontWeight: activeTab === tab ? 700 : 400,
              fontSize: "0.88rem",
              color: activeTab === tab ? BRAND_GREEN : "#6c757d",
              cursor: "pointer",
              textTransform: "capitalize",
              letterSpacing: "-0.1px",
            }}
          >
            {tab}
            <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", fontWeight: 400, color: activeTab === tab ? BRAND_GREEN : "#adb5bd" }}>
              ({tabCount(tab)})
            </span>
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: "center", color: "#6c757d", padding: "3rem" }}>Loading…</div>
      )}

      {!loading && activeTab === "digital" && (
        <>
          {digitalSilver.length + digitalGold.length === 0 && (
            <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#6c757d" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📭</div>
              <p style={{ margin: 0, fontSize: "0.9rem" }}>{emptyMessage}</p>
            </div>
          )}
          <PackageTable title="Silver" rows={digitalSilver} {...sharedTableProps} />
          <PackageTable title="Gold"   rows={digitalGold}   {...sharedTableProps} />
        </>
      )}

      {!loading && activeTab === "paper" && (
        <>
          {paperVipBronze.length + paperSilver.length + paperGold.length + paperWvVip.length + replacement.length === 0 && (
            <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#6c757d" }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>📭</div>
              <p style={{ margin: 0, fontSize: "0.9rem" }}>{emptyMessage}</p>
            </div>
          )}
          <PackageTable title="VIP &amp; Bronze"      rows={paperVipBronze} {...sharedTableProps} />
          <PackageTable title="Silver"               rows={paperSilver}    {...sharedTableProps} />
          <PackageTable title="Gold"                 rows={paperGold}      {...sharedTableProps} />
          <PackageTable title="WV VIP Card"          rows={paperWvVip}     {...sharedTableProps} />
          <PackageTable title="Replacement VIP Card" rows={replacement}    {...sharedTableProps} />
        </>
      )}
    </div>
  );
};

export default BulkVipPackages;
